/**
 * Comprobante consolidado: un solo papel para varios pagos.
 *
 * El caso es de ventanilla: alguien paga por tres estudiantes y quiere UN
 * comprobante, no tres. Los alumnos no tienen por qué ser familia —un
 * patrocinador, un grupo becado, hermanos con apellidos distintos—, así que la
 * pantalla no agrupa por parentesco: se eligen a mano.
 *
 * Los pagos NO se crean aquí. Ya están registrados, cada uno con su consecutivo y
 * su efecto sobre el saldo de su estudiante; esta pantalla solo los imprime
 * juntos. Por eso la lista sale del almacén local y el único momento que necesita
 * conexión es generar el PDF.
 */

import { useCallback, useEffect, useState } from "react";
import { MAX_CONSOLIDATED_PAYMENTS } from "@senm/contracts";
import { formatMoney, fromRow } from "@senm/domain";
import { ApiRequestError, apiDownload } from "../api/client.js";
import {
  db,
  normalizeForSearch,
  type LocalPayment,
  type LocalStudent,
} from "../local/db.js";
import { openPdf } from "../services/payments.js";
import type { StoredSession } from "../local/session.js";

export function ConsolidatedReceiptPage(props: {
  session: StoredSession;
  conexion: string;
}): JSX.Element {
  const [alumnos, setAlumnos] = useState<LocalStudent[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [sugerencias, setSugerencias] = useState<LocalStudent[]>([]);
  const [pagos, setPagos] = useState<LocalPayment[]>([]);
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);

  /** Busca en el almacén local: los estudiantes ya están en el dispositivo. */
  useEffect(() => {
    const texto = busqueda.trim();
    if (texto === "") {
      setSugerencias([]);
      return;
    }
    let vigente = true;
    void (async () => {
      const aguja = normalizeForSearch(texto);
      const todos = await db.students.toArray();
      const yaEstan = new Set(alumnos.map((alumno) => alumno.id));
      const encontrados = todos
        .filter(
          (alumno) =>
            !yaEstan.has(alumno.id) &&
            (alumno.searchName.includes(aguja) ||
              alumno.code.toUpperCase().includes(aguja)),
        )
        .slice(0, 8);
      if (vigente) setSugerencias(encontrados);
    })();
    return () => {
      vigente = false;
    };
  }, [busqueda, alumnos]);

  /** Pagos de los estudiantes elegidos, los más recientes primero. */
  const cargarPagos = useCallback(async () => {
    if (alumnos.length === 0) {
      setPagos([]);
      return;
    }
    const identificadores = new Set(alumnos.map((alumno) => alumno.id));
    const todos = await db.payments.toArray();
    setPagos(
      todos
        .filter((pago) => identificadores.has(pago.studentId))
        // Un pago anulado no puede entrar en un comprobante: diría que se cobró algo
        // que ya no está cobrado.
        .filter((pago) => pago.status !== "ANULADO")
        .sort((uno, otro) => otro.capturedAt.localeCompare(uno.capturedAt)),
    );
  }, [alumnos]);

  useEffect(() => {
    void cargarPagos();
  }, [cargarPagos]);

  // Al quitar un estudiante, sus pagos dejan de estar marcados: si no, el
  // comprobante incluiría a alguien que ya no aparece en la pantalla.
  useEffect(() => {
    const disponibles = new Set(pagos.map((pago) => pago.id));
    setElegidos((previos) => previos.filter((id) => disponibles.has(id)));
  }, [pagos]);

  const alternar = (id: string): void => {
    setElegidos((previos) =>
      previos.includes(id)
        ? previos.filter((otro) => otro !== id)
        : previos.length >= MAX_CONSOLIDATED_PAYMENTS
          ? previos
          : [...previos, id],
    );
  };

  const generar = async (): Promise<void> => {
    setGenerando(true);
    setError(null);
    try {
      const archivo = await apiDownload(
        `/v1/receipts/consolidated?paymentIds=${elegidos.join(",")}`,
      );
      openPdf(archivo.bytes, archivo.fileName);
    } catch (problema) {
      // El 404 tiene una causa concreta y accionable: alguno de los pagos todavía
      // solo existe en este dispositivo. Decir «no fue posible» dejaría al cajero
      // sin saber que le basta con sincronizar para resolverlo.
      const noConocido =
        problema instanceof ApiRequestError && problema.httpStatus === 404;
      setError(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? "El comprobante consolidado necesita conexión con el servidor."
          : noConocido
            ? "Alguno de los pagos aún no ha subido al servidor. Sincronice y vuelva a intentarlo."
            : "No fue posible generar el comprobante.",
      );
    } finally {
      setGenerando(false);
    }
  };

  const total = pagos
    .filter((pago) => elegidos.includes(pago.id))
    .reduce((suma, pago) => suma + pago.amountMinor, 0);
  const moneda =
    pagos.find((pago) => elegidos.includes(pago.id))?.currency ?? "GTQ";

  return (
    <>
      <h1>Comprobante consolidado</h1>
      <p className="ayuda">
        Un solo papel para varios pagos ya registrados. Los estudiantes no
        tienen que ser familia. Cada pago conserva su propio consecutivo y su
        efecto sobre el saldo.
      </p>

      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      <fieldset className="modos">
        <legend>Estudiantes</legend>

        {alumnos.length > 0 && (
          <ul className="fichas-seccion">
            {alumnos.map((alumno) => (
              <li key={alumno.id} className="ficha-seccion">
                <span>
                  {alumno.code} · {alumno.fullName}
                </span>
                <button
                  type="button"
                  className="boton compacto"
                  onClick={() =>
                    setAlumnos((previos) =>
                      previos.filter((otro) => otro.id !== alumno.id),
                    )
                  }
                >
                  Quitar
                  <span className="oculto-visual"> a {alumno.fullName}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <label>
          <span>Añadir estudiante por código o nombre</span>
          <input
            type="search"
            value={busqueda}
            onChange={(evento) => setBusqueda(evento.target.value)}
            placeholder="Código o nombre"
          />
        </label>

        {busqueda.trim() !== "" && sugerencias.length === 0 && (
          <p className="ayuda">Ningún estudiante coincide.</p>
        )}

        {sugerencias.length > 0 && (
          <ul className="fichas-seccion">
            {sugerencias.map((alumno) => (
              <li key={alumno.id}>
                <button
                  type="button"
                  className="boton compacto"
                  onClick={() => {
                    setAlumnos((previos) => [...previos, alumno]);
                    setBusqueda("");
                  }}
                >
                  + {alumno.code} · {alumno.fullName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      <h2>Pagos a incluir</h2>

      {alumnos.length === 0 ? (
        <p className="vacio">Elija primero uno o más estudiantes.</p>
      ) : pagos.length === 0 ? (
        <p className="vacio">
          Los estudiantes elegidos no tienen pagos registrados en este
          dispositivo.
        </p>
      ) : (
        <div className="tarjeta tabla-desplazable">
          <table>
            <caption className="oculto-visual">
              Pagos de los estudiantes elegidos, para incluir en el comprobante
            </caption>
            <thead>
              <tr>
                <th scope="col">Incluir</th>
                <th scope="col">Estudiante</th>
                <th scope="col">Fecha</th>
                <th scope="col">Recibo</th>
                <th scope="col">Estado</th>
                <th scope="col" className="numero">
                  Importe
                </th>
              </tr>
            </thead>
            <tbody>
              {pagos.map((pago) => (
                <tr key={pago.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={elegidos.includes(pago.id)}
                      onChange={() => alternar(pago.id)}
                      aria-label={`Incluir el pago de ${pago.studentName} del ${pago.operationalDate}`}
                    />
                  </td>
                  <td className="fuerte">{pago.studentName}</td>
                  <td>{pago.operationalDate}</td>
                  <td>{pago.receiptNumber ?? "Sin número"}</td>
                  <td>{pago.status}</td>
                  <td className="numero">
                    {formatMoney(fromRow(pago.amountMinor, pago.currency))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {elegidos.length > 0 && (
        <div className="tarjeta dato">
          <span className="etiqueta">Total del comprobante</span>
          <span className="valor">{formatMoney(fromRow(total, moneda))}</span>
          <p className="ayuda">
            {elegidos.length} pago(s) de{" "}
            {
              new Set(
                pagos
                  .filter((p) => elegidos.includes(p.id))
                  .map((p) => p.studentId),
              ).size
            }{" "}
            estudiante(s).
          </p>
        </div>
      )}

      {elegidos.length >= MAX_CONSOLIDATED_PAYMENTS && (
        <p className="ayuda">
          Máximo {MAX_CONSOLIDATED_PAYMENTS} pagos por comprobante: pasado eso
          el papel deja de ser algo que alguien revisa en el mostrador.
        </p>
      )}

      <div className="pila">
        <button
          type="button"
          className="boton primario"
          onClick={() => void generar()}
          disabled={
            elegidos.length === 0 || generando || props.conexion !== "EN_LINEA"
          }
        >
          {generando ? "Generando…" : "Generar comprobante"}
        </button>
      </div>

      {props.conexion !== "EN_LINEA" && (
        <p className="ayuda">
          El comprobante consolidado se genera en el servidor, así que necesita
          conexión.
        </p>
      )}
    </>
  );
}
